import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { CheckCircle2, ChevronRight, Loader2, Plus, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { COUNTRIES } from "@/lib/countries";
import {
  apiGetPreboardingContext,
  apiPreboardingColleagues,
  apiPreboardingComplete,
  apiPreboardingConfirmCorporateDoc,
  apiPreboardingPersonalDetails,
  apiPreboardingUploadAvatar,
  apiPreboardingUploadDocument,
  type PreboardingColleague,
  type PreboardingCorporateDoc,
  type PreboardingContext,
} from "@/lib/api";

type Step = 1 | 2 | 3 | 4;

// ── Helpers ───────────────────────────────────────────────────────────────────

function StepIndicator({ step }: { step: Step }) {
  const { t } = useTranslation();
  const labels = [
    t("preboarding.stepPhoto"),
    t("preboarding.stepPersonal"),
    t("preboarding.stepDocuments"),
    t("preboarding.stepWelcome"),
  ];
  return (
    <div className="flex items-center justify-center gap-2 mb-8">
      {labels.map((label, i) => {
        const idx = i + 1;
        const active = idx === step;
        const done = idx < step;
        return (
          <div key={idx} className="flex items-center gap-2">
            <div className="flex flex-col items-center gap-1">
              <div
                className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                  done
                    ? "bg-primary text-primary-foreground"
                    : active
                    ? "bg-primary text-primary-foreground ring-4 ring-primary/20"
                    : "bg-muted text-muted-foreground"
                }`}
              >
                {done ? <CheckCircle2 className="h-4 w-4" /> : idx}
              </div>
              <span className={`text-xs hidden sm:block ${active ? "text-foreground font-medium" : "text-muted-foreground"}`}>
                {label}
              </span>
            </div>
            {i < 3 && <div className={`h-px w-8 sm:w-12 ${idx < step ? "bg-primary" : "bg-border"}`} />}
          </div>
        );
      })}
    </div>
  );
}

function AvatarInitials({ name, color }: { name: string; color: string }) {
  const initials = name
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <div
      className="h-full w-full rounded-full flex items-center justify-center text-white font-semibold text-xl select-none"
      style={{ background: color }}
    >
      {initials}
    </div>
  );
}

// ── Step 1: Photo ─────────────────────────────────────────────────────────────

function PreBoardingPhotoStep({
  tenantSlug,
  token,
  employeeName,
  onNext,
}: {
  tenantSlug: string;
  token: string;
  employeeName: string;
  onNext: () => void;
}) {
  const { t } = useTranslation();
  const [preview, setPreview] = useState<string | null>(null);
  const [uploaded, setUploaded] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const uploadMutation = useMutation({
    mutationFn: (file: File) => apiPreboardingUploadAvatar(tenantSlug, token, file),
    onSuccess: () => setUploaded(true),
    onError: () => toast.error("Upload failed. Please try again."),
  });

  const handleFile = useCallback(
    (file: File) => {
      const url = URL.createObjectURL(file);
      setPreview(url);
      uploadMutation.mutate(file);
    },
    [uploadMutation],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile],
  );

  return (
    <div className="max-w-lg mx-auto text-center space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground">{t("preboarding.photoTitle")}</h2>
        <p className="text-muted-foreground mt-2">{t("preboarding.photoSubtitle")}</p>
      </div>

      {uploaded ? (
        <div className="space-y-4">
          {preview && (
            <div className="mx-auto h-32 w-32 rounded-full overflow-hidden ring-4 ring-primary/30">
              <img src={preview} alt="Profile" className="h-full w-full object-cover" />
            </div>
          )}
          <p className="text-lg font-semibold text-foreground animate-in fade-in slide-in-from-bottom-2">
            <span className="animate-bounce inline-block">🎉</span>{" "}
            {t("preboarding.photoSuccess")}
          </p>
          <Button size="lg" onClick={onNext} className="w-full sm:w-auto">
            {t("preboarding.photoContinue")} <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {preview ? (
            <div className="mx-auto h-32 w-32 rounded-full overflow-hidden ring-4 ring-border">
              <img src={preview} alt="Preview" className="h-full w-full object-cover" />
            </div>
          ) : (
            <div
              className="mx-auto h-40 w-40 rounded-full border-2 border-dashed border-border bg-muted/30 flex flex-col items-center justify-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors"
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
            >
              <Upload className="h-8 w-8 text-muted-foreground" />
              <span className="text-xs text-muted-foreground mt-2 px-4 text-center">{t("preboarding.photoUploadPrompt")}</span>
            </div>
          )}

          {uploadMutation.isPending && (
            <div className="flex items-center justify-center gap-2 text-muted-foreground text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              Uploading…
            </div>
          )}

          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
          />
          <p className="text-xs text-muted-foreground">{t("preboarding.photoFormats")}</p>

          <button
            type="button"
            onClick={onNext}
            className="text-sm text-muted-foreground hover:text-foreground underline underline-offset-4 transition-colors"
          >
            {t("preboarding.photoSkip")}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Step 2: Personal Details ──────────────────────────────────────────────────

type PersonalForm = {
  phone: string;
  address: string;
  nationality: string;
  birthday: string;
  bankName: string;
  bankAccountNumber: string;
  iban: string;
  bicSwift: string;
  accountHolderName: string;
  emergencyContactName: string;
  emergencyRelationship: string;
  emergencyPhone: string;
  emergencyEmail: string;
  children: Array<{ firstName: string; dateOfBirth: string }>;
};

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-border/60 bg-card p-5 space-y-4">
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      {children}
    </div>
  );
}

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function PreBoardingPersonalStep({
  tenantSlug,
  token,
  companyCountry,
  onNext,
}: {
  tenantSlug: string;
  token: string;
  companyCountry: string | null;
  onNext: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<PersonalForm>({
    phone: "",
    address: "",
    nationality: "",
    birthday: "",
    bankName: "",
    bankAccountNumber: "",
    iban: "",
    bicSwift: "",
    accountHolderName: "",
    emergencyContactName: "",
    emergencyRelationship: "",
    emergencyPhone: "",
    emergencyEmail: "",
    children: [],
  });

  const f = (key: keyof PersonalForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const isMacedoniaCountry = /north.?mac|macedonia|^mk$/i.test(companyCountry ?? "");

  const saveMutation = useMutation({
    mutationFn: () =>
      apiPreboardingPersonalDetails(tenantSlug, token, {
        phone: form.phone || undefined,
        address: form.address || undefined,
        nationality: form.nationality || undefined,
        birthday: form.birthday || undefined,
        bankDetails: {
          bankName: form.bankName || undefined,
          iban: isMacedoniaCountry ? (form.bankAccountNumber || undefined) : (form.iban || undefined),
          bicSwift: isMacedoniaCountry ? undefined : (form.bicSwift || undefined),
          accountHolderName: form.accountHolderName || undefined,
        },
        emergencyContact: form.emergencyContactName
          ? {
              contactName: form.emergencyContactName,
              relationship: form.emergencyRelationship || undefined,
              phone: form.emergencyPhone || undefined,
              email: form.emergencyEmail || undefined,
            }
          : undefined,
        children: form.children.filter((c) => c.firstName.trim()),
      }),
    onSuccess: onNext,
    onError: () => toast.error("Failed to save. Please try again."),
  });

  return (
    <div className="max-w-2xl mx-auto space-y-5">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-foreground">{t("preboarding.personalTitle")}</h2>
        <p className="text-muted-foreground mt-1">{t("preboarding.personalSubtitle")}</p>
      </div>

      <SectionCard title={t("preboarding.contactInfo")}>
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField label={t("preboarding.phone")}>
            <Input type="tel" value={form.phone} onChange={f("phone")} placeholder="+1 555 000 0000" />
          </FormField>
          <FormField label={t("preboarding.nationality")}>
            <select
              value={form.nationality}
              onChange={(e) => setForm((prev) => ({ ...prev, nationality: e.target.value }))}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">—</option>
              {COUNTRIES.map((c) => (
                <option key={c.value} value={c.label}>{c.label}</option>
              ))}
            </select>
          </FormField>
          <FormField label={t("preboarding.birthday")}>
            <Input type="date" value={form.birthday} onChange={f("birthday")} max={new Date().toISOString().slice(0, 10)} />
          </FormField>
          <FormField label={t("preboarding.address")}>
            <Textarea value={form.address} onChange={f("address")} placeholder="Street, City, Country" className="resize-none h-10 py-2" />
          </FormField>
        </div>
      </SectionCard>

      <SectionCard title={t("preboarding.bankDetailsTitle")}>
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField label={t("preboarding.bankName")}>
            <Input value={form.bankName} onChange={f("bankName")} />
          </FormField>
          <FormField label={t("preboarding.accountHolderName")}>
            <Input value={form.accountHolderName} onChange={f("accountHolderName")} />
          </FormField>
          {isMacedoniaCountry ? (
            <FormField label={t("preboarding.bankAccountNumber")}>
              <Input value={form.bankAccountNumber} onChange={f("bankAccountNumber")} className="font-mono" />
            </FormField>
          ) : (
            <>
              <FormField label={t("preboarding.iban")}>
                <Input value={form.iban} onChange={f("iban")} placeholder="GB29 NWBK 6016 1331 9268 19" className="font-mono" />
              </FormField>
              <FormField label={t("preboarding.bicSwift")}>
                <Input value={form.bicSwift} onChange={f("bicSwift")} placeholder="NWBKGB2L" className="font-mono" />
              </FormField>
            </>
          )}
        </div>
      </SectionCard>

      <SectionCard title={t("preboarding.emergencyContactTitle")}>
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField label={t("preboarding.contactName")}>
            <Input value={form.emergencyContactName} onChange={f("emergencyContactName")} />
          </FormField>
          <FormField label={t("preboarding.relationship")}>
            <Input value={form.emergencyRelationship} onChange={f("emergencyRelationship")} />
          </FormField>
          <FormField label={t("preboarding.emergencyPhone")}>
            <Input type="tel" value={form.emergencyPhone} onChange={f("emergencyPhone")} />
          </FormField>
          <FormField label={t("preboarding.emergencyEmail")}>
            <Input type="email" value={form.emergencyEmail} onChange={f("emergencyEmail")} />
          </FormField>
        </div>
      </SectionCard>

      <SectionCard title={t("preboarding.childrenTitle")}>
        <div className="space-y-3">
          {form.children.map((child, i) => (
            <div key={i} className="flex gap-2 items-end">
              <FormField label={t("preboarding.childFirstName")}>
                <Input
                  value={child.firstName}
                  onChange={(e) => {
                    const updated = [...form.children];
                    updated[i] = { ...updated[i], firstName: e.target.value };
                    setForm((prev) => ({ ...prev, children: updated }));
                  }}
                />
              </FormField>
              <FormField label={t("preboarding.childDateOfBirth")}>
                <Input
                  type="date"
                  max={new Date().toISOString().slice(0, 10)}
                  value={child.dateOfBirth}
                  onChange={(e) => {
                    const updated = [...form.children];
                    updated[i] = { ...updated[i], dateOfBirth: e.target.value };
                    setForm((prev) => ({ ...prev, children: updated }));
                  }}
                />
              </FormField>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="mb-0.5 shrink-0"
                onClick={() => setForm((prev) => ({ ...prev, children: prev.children.filter((_, j) => j !== i) }))}
              >
                <Trash2 className="h-4 w-4 text-muted-foreground" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setForm((prev) => ({ ...prev, children: [...prev.children, { firstName: "", dateOfBirth: "" }] }))}
          >
            <Plus className="h-4 w-4 mr-1.5" />
            {t("preboarding.addChild")}
          </Button>
        </div>
      </SectionCard>

      <div className="flex justify-end pt-2">
        <Button size="lg" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
          {saveMutation.isPending ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t("preboarding.saving")}</>
          ) : (
            <>{t("preboarding.continue")} <ChevronRight className="ml-1 h-4 w-4" /></>
          )}
        </Button>
      </div>
    </div>
  );
}

// ── Step 3: Documents ─────────────────────────────────────────────────────────

function DocumentUploadBox({
  label,
  uploaded,
  uploading,
  uploadedLabel,
  onFile,
}: {
  label: string;
  uploaded: boolean;
  uploading: boolean;
  uploadedLabel: string;
  onFile: (file: File) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div
      className={`relative rounded-xl border-2 border-dashed p-6 flex flex-col items-center gap-3 cursor-pointer transition-all ${
        uploaded
          ? "border-emerald-400 bg-emerald-50 dark:bg-emerald-950/20"
          : "border-border hover:border-primary hover:bg-primary/5"
      }`}
      onClick={() => !uploaded && inputRef.current?.click()}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); }}
      />
      {uploading ? (
        <Loader2 className="h-8 w-8 text-muted-foreground animate-spin" />
      ) : uploaded ? (
        <CheckCircle2 className="h-8 w-8 text-emerald-500" />
      ) : (
        <Upload className="h-8 w-8 text-muted-foreground" />
      )}
      <span className={`text-sm font-medium text-center ${uploaded ? "text-emerald-700 dark:text-emerald-400" : "text-foreground"}`}>
        {uploaded ? uploadedLabel : label}
      </span>
      {!uploaded && <span className="text-xs text-muted-foreground">PDF, JPG or PNG</span>}
    </div>
  );
}

function PreBoardingDocumentsStep({
  tenantSlug,
  token,
  corporateDocs,
  onNext,
  onDocsUpdate,
}: {
  tenantSlug: string;
  token: string;
  corporateDocs: PreboardingCorporateDoc[];
  onNext: () => void;
  onDocsUpdate: (docs: PreboardingCorporateDoc[]) => void;
}) {
  const { t } = useTranslation();
  const [idUploaded, setIdUploaded] = useState(false);
  const [bankUploaded, setBankUploaded] = useState(false);
  const [openedIds, setOpenedIds] = useState<Set<number>>(new Set());
  const [confirmedIds, setConfirmedIds] = useState<Set<number>>(
    new Set(corporateDocs.filter((d) => d.confirmedAt).map((d) => d.id)),
  );
  const [confirmingId, setConfirmingId] = useState<number | null>(null);

  const idMutation = useMutation({
    mutationFn: (file: File) => apiPreboardingUploadDocument(tenantSlug, token, "id_card", file),
    onSuccess: () => setIdUploaded(true),
    onError: () => toast.error("Upload failed. Please try again."),
  });

  const bankMutation = useMutation({
    mutationFn: (file: File) => apiPreboardingUploadDocument(tenantSlug, token, "bank_card", file),
    onSuccess: () => setBankUploaded(true),
    onError: () => toast.error("Upload failed. Please try again."),
  });

  const allConfirmed = corporateDocs.length === 0 || corporateDocs.every((d) => confirmedIds.has(d.id));

  const handleConfirm = async (doc: PreboardingCorporateDoc) => {
    setConfirmingId(doc.id);
    try {
      await apiPreboardingConfirmCorporateDoc(tenantSlug, token, doc.id);
      setConfirmedIds((prev) => new Set([...prev, doc.id]));
      onDocsUpdate(corporateDocs.map((d) => (d.id === doc.id ? { ...d, confirmedAt: new Date().toISOString() } : d)));
    } catch {
      toast.error("Failed to confirm. Please try again.");
    } finally {
      setConfirmingId(null);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-foreground">{t("preboarding.documentsTitle")}</h2>
        <p className="text-muted-foreground mt-1">{t("preboarding.documentsSubtitle")}</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <DocumentUploadBox
          label={t("preboarding.uploadIdCard")}
          uploaded={idUploaded}
          uploading={idMutation.isPending}
          uploadedLabel={t("preboarding.idCardUploaded")}
          onFile={(f) => idMutation.mutate(f)}
        />
        <DocumentUploadBox
          label={t("preboarding.uploadBankCard")}
          uploaded={bankUploaded}
          uploading={bankMutation.isPending}
          uploadedLabel={t("preboarding.bankCardUploaded")}
          onFile={(f) => bankMutation.mutate(f)}
        />
      </div>

      <div className="rounded-xl border border-border/60 bg-muted/20 px-4 py-3 text-sm text-muted-foreground">
        📄 {t("preboarding.cvCopiedNotice")}
      </div>

      {corporateDocs.length > 0 && (
        <div className="space-y-3">
          <div>
            <h3 className="text-sm font-semibold text-foreground">{t("preboarding.corporateDocsTitle")}</h3>
            <p className="text-xs text-muted-foreground mt-0.5">{t("preboarding.corporateDocsSubtitle")}</p>
          </div>
          {corporateDocs.map((doc) => {
            const confirmed = confirmedIds.has(doc.id);
            const opened = openedIds.has(doc.id);
            return (
              <div
                key={doc.id}
                className={`flex items-center gap-3 rounded-xl border p-4 transition-colors ${
                  confirmed ? "border-emerald-300 bg-emerald-50 dark:bg-emerald-950/20" : "border-border bg-card"
                }`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{doc.documentName}</p>
                </div>
                {confirmed ? (
                  <span className="text-xs font-medium text-emerald-600 flex items-center gap-1">
                    <CheckCircle2 className="h-4 w-4" />
                    {t("preboarding.documentConfirmed")}
                  </span>
                ) : (
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setOpenedIds((prev) => new Set([...prev, doc.id]));
                        window.open(doc.fileUrl, "_blank");
                      }}
                    >
                      {t("preboarding.openDocument")}
                    </Button>
                    <Button
                      size="sm"
                      disabled={!opened || confirmingId === doc.id}
                      onClick={() => handleConfirm(doc)}
                    >
                      {confirmingId === doc.id ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        t("preboarding.confirmRead")
                      )}
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="flex justify-end pt-2">
        <Button size="lg" onClick={onNext} disabled={!allConfirmed} title={!allConfirmed ? t("preboarding.continueDisabledDocs") : undefined}>
          {t("preboarding.continue")} <ChevronRight className="ml-1 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

// ── Step 4: Welcome ───────────────────────────────────────────────────────────

function ColleagueCard({ colleague }: { colleague: PreboardingColleague }) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-xl border border-border/60 bg-card p-4 text-center">
      <div className="h-14 w-14 rounded-full overflow-hidden ring-2 ring-border/60">
        {colleague.avatarUrl ? (
          <img src={colleague.avatarUrl} alt={colleague.name} className="h-full w-full object-cover" />
        ) : (
          <AvatarInitials name={colleague.name} color={colleague.avatarColor} />
        )}
      </div>
      <div>
        <p className="text-sm font-semibold text-foreground">{colleague.name}</p>
        {colleague.jobTitle && <p className="text-xs text-muted-foreground">{colleague.jobTitle}</p>}
      </div>
    </div>
  );
}

function PreBoardingWelcomeStep({
  tenantSlug,
  token,
  employeeName,
}: {
  tenantSlug: string;
  token: string;
  employeeName: string;
}) {
  const { t } = useTranslation();
  const completedRef = useRef(false);

  useEffect(() => {
    if (completedRef.current) return;
    completedRef.current = true;
    apiPreboardingComplete(tenantSlug, token).catch(() => {});
  }, [tenantSlug, token]);

  const { data: colleagues = [] } = useQuery({
    queryKey: ["preboarding-colleagues", tenantSlug, token],
    queryFn: () => apiPreboardingColleagues(tenantSlug, token),
    staleTime: Infinity,
  });

  return (
    <div className="max-w-2xl mx-auto text-center space-y-8">
      <div className="space-y-3">
        <div className="text-6xl animate-bounce">🎉</div>
        <h2 className="text-3xl font-bold text-foreground">{t("preboarding.welcomeTitle")}</h2>
        <p className="text-muted-foreground text-lg max-w-md mx-auto">{t("preboarding.welcomeSubtitle")}</p>
      </div>

      {colleagues.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-base font-semibold text-foreground">{t("preboarding.colleaguesTitle")}</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {colleagues.map((c, i) => (
              <ColleagueCard key={i} colleague={c} />
            ))}
          </div>
        </div>
      )}

      {colleagues.length === 0 && (
        <p className="text-sm text-muted-foreground">{t("preboarding.noColleagues")}</p>
      )}

      <div className="rounded-xl border border-border/60 bg-muted/30 px-6 py-4">
        <p className="text-sm text-muted-foreground">{t("preboarding.loginInstructions")}</p>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function PreBoarding() {
  const { tenantSlug = "", token = "" } = useParams<{ tenantSlug: string; token: string }>();
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>(1);
  const [corporateDocs, setCorporateDocs] = useState<PreboardingCorporateDoc[]>([]);

  const { data: ctx, isLoading, error } = useQuery<PreboardingContext>({
    queryKey: ["preboarding-context", tenantSlug, token],
    queryFn: () => apiGetPreboardingContext(tenantSlug, token),
    retry: false,
  });

  useEffect(() => {
    if (ctx?.corporateDocs) setCorporateDocs(ctx.corporateDocs);
    if (ctx?.isCompleted) setStep(4);
  }, [ctx]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !ctx) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-sm text-center space-y-3">
          <div className="text-4xl">🔒</div>
          <p className="text-base font-medium text-foreground">{t("preboarding.tokenExpired")}</p>
        </div>
      </div>
    );
  }

  const employeeName = `${ctx.firstName} ${ctx.lastName}`.trim();

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-background">
      {/* Header */}
      <header className="border-b border-border/60 bg-background/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {ctx.logoUrl ? (
              <img src={ctx.logoUrl} alt={ctx.companyName ?? ""} className="h-8 object-contain max-w-32" />
            ) : (
              <span className="font-semibold text-foreground">{ctx.companyName}</span>
            )}
          </div>
          <span className="text-xs text-muted-foreground">Powered by BestHR</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-10">
        {step !== 4 && <StepIndicator step={step} />}

        {step === 1 && (
          <PreBoardingPhotoStep
            tenantSlug={tenantSlug}
            token={token}
            employeeName={employeeName}
            onNext={() => setStep(2)}
          />
        )}
        {step === 2 && (
          <PreBoardingPersonalStep
            tenantSlug={tenantSlug}
            token={token}
            companyCountry={ctx?.companyCountry ?? null}
            onNext={() => setStep(3)}
          />
        )}
        {step === 3 && (
          <PreBoardingDocumentsStep
            tenantSlug={tenantSlug}
            token={token}
            corporateDocs={corporateDocs}
            onNext={() => setStep(4)}
            onDocsUpdate={setCorporateDocs}
          />
        )}
        {step === 4 && (
          <PreBoardingWelcomeStep
            tenantSlug={tenantSlug}
            token={token}
            employeeName={employeeName}
          />
        )}
      </main>
    </div>
  );
}
